import { useRef } from "react";

// Shared IntersectionObserver — a single observer watches every element registered via
// `useScrollReveal()`. Toggles the `.is-visible` class when each element enters the
// viewport (10% threshold) so CSS can animate it in. One observer for the whole page
// instead of one-per-element keeps things fast even on a 500-series catalog.
let sharedObserver = null;
function getObserver() {
  if (sharedObserver || typeof window === "undefined" || !("IntersectionObserver" in window)) {
    return sharedObserver;
  }
  sharedObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          // Once visible we stop watching — animation is one-shot.
          sharedObserver.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
  );
  return sharedObserver;
}

/**
 * Adds `.reveal-on-scroll` to the element and registers it with the shared observer.
 * When the element enters the viewport, `.is-visible` is added → CSS animates it in.
 * Returns the ref to attach to your element.
 *
 * DISABLED per user request — they found the staggered fade-in on the catalog made
 * scrolling feel laggy and unnecessary. The hook is now a no-op: returns a ref so
 * existing call sites don't break, but never adds the reveal class. All elements
 * render at full opacity immediately. The CSS rule below is also neutralised so even
 * elements that were given `.reveal-on-scroll` directly (without the hook) stay
 * visible.
 */
export function useScrollReveal() {
  const ref = useRef(null);
  // Intentionally no-op: we keep the ref API so callers don't need to change, but
  // no IntersectionObserver is registered and no class is added.
  return ref;
}
